import React, { useState, useEffect } from 'react';
import { EmptyState, F } from './shared';

const GRADIENT = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const GRADIENT_TEXT = {
  background: GRADIENT,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const avatarColors = [
  { bg: '#e0e7ff', color: '#3730a3' },
  { bg: '#dcfce7', color: '#15803d' },
  { bg: '#fce7f3', color: '#9d174d' },
  { bg: '#fff7ed', color: '#c2410c' },
  { bg: '#f0f9ff', color: '#0369a1' },
];

const BattleCards = ({ battleCards }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',
    }}>
      {/* ── Page header ── */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        marginBottom: isMobile ? 16 : (isTablet ? 20 : 24) 
      }}>
        <h2 style={{
          fontFamily: "'Sansation', sans-serif",
          fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
          fontWeight: 800,
          letterSpacing: '-0.03em',
          color: '#0f172a',
          margin: 0,
        }}>
          Battle Cards
        </h2>
      </div>

      {battleCards.length > 0 ? (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: isMobile ? 14 : 20 
        }}>
          {battleCards.map((card, i) => {
            const letter = (card.title || '?')[0].toUpperCase();
            const av = avatarColors[i % avatarColors.length];
            const winProbMatch = (card.winStrategy || card.winProb || '').match(/(\d{2,3})%/);
            const winProb = card.winProb || (winProbMatch ? winProbMatch[1] + '%' : null);

            return (
              <div key={i} style={{
                background: '#ffffff',
                border: '0.5px solid rgba(0,0,0,0.06)',
                borderRadius: isMobile ? 14 : (isTablet ? 16 : 20), 
                overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}>

                {/* ── Card header ── */}
                <div style={{
                  display: 'flex', 
                  alignItems: isMobile ? 'flex-start' : 'center', 
                  justifyContent: 'space-between',
                  padding: isMobile ? '16px 16px' : (isTablet ? '18px 20px' : '20px 24px'),
                  borderBottom: '0.5px solid rgba(0,0,0,0.06)',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 12 : 0,
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: isMobile ? 12 : 16,
                    width: isMobile ? '100%' : 'auto',
                  }}>
                    <div style={{
                      width: isMobile ? 42 : 48, 
                      height: isMobile ? 42 : 48, 
                      borderRadius: isMobile ? 10 : 12, 
                      flexShrink: 0,
                      background: av.bg, 
                      color: av.color,
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontFamily: "'Sansation', sans-serif",
                      fontSize: isMobile ? 18 : 20, 
                      fontWeight: 800,
                    }}>
                      {letter}
                    </div>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{
                        fontFamily: F.mono,
                        fontSize: isMobile ? 8 : 9, 
                        fontWeight: 700,
                        letterSpacing: '0.12em', 
                        textTransform: 'uppercase',
                        color: '#94a3b8', 
                        margin: isMobile ? '0 0 3px' : '0 0 4px',
                      }}>
                        VS Competitor
                      </p>
                      <p style={{
                        fontFamily: "'Sansation', sans-serif",
                        fontSize: isMobile ? 16 : 18, 
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        color: '#0f172a', 
                        margin: 0,
                        wordBreak: 'break-word',
                      }}>
                        {card.title}
                      </p>
                    </div>
                  </div>

                  {winProb && (
                    <div style={{ 
                      textAlign: isMobile ? 'left' : 'right',
                      width: isMobile ? '100%' : 'auto',
                      paddingLeft: isMobile ? 54 : 0,
                    }}>
                      <p style={{
                        fontFamily: F.mono,
                        fontSize: isMobile ? 8 : 9, 
                        fontWeight: 700,
                        letterSpacing: '0.12em', 
                        textTransform: 'uppercase',
                        color: '#94a3b8', 
                        margin: isMobile ? '0 0 3px' : '0 0 4px',
                      }}>
                        Win Prob.
                      </p>
                      <p style={{
                        fontFamily: F.mono,
                        fontSize: isMobile ? 20 : 22, 
                        fontWeight: 800,
                        color: '#16a34a', 
                        margin: 0,
                      }}>
                        {winProb}
                      </p>
                    </div>
                  )}
                </div>

                {/* ── 3-col intelligence grid ── */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', 
                  gap: isMobile ? 10 : 14, 
                  padding: isMobile ? '16px 16px' : (isTablet ? '18px 20px' : '20px 24px') 
                }}>
                  {[
                    { key: 'strength',       label: 'Their Strength',     color: null,      bg: '#f0f4ff', border: '#dde3f5' },
                    { key: 'weakness',       label: 'Their Weakness',     color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
                    { key: 'differentiator', label: 'Our Differentiator', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
                  ].map(col => card[col.key] && (
                    <div key={col.key} style={{
                      padding: isMobile ? '14px 14px' : '16px 18px', 
                      borderRadius: isMobile ? 10 : 12,
                      background: col.bg,
                      border: col.color ? `1px solid ${col.border}` : '1px solid #c7d7f5',
                      minWidth: 0,
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: isMobile ? 6 : 7, 
                        marginBottom: isMobile ? 10 : 12 
                      }}>
                        {col.color ? (
                          <span style={{ 
                            width: isMobile ? 7 : 8, 
                            height: isMobile ? 7 : 8, 
                            borderRadius: '50%', 
                            background: col.color, 
                            flexShrink: 0 
                          }} />
                        ) : (
                          <span style={{ 
                            width: isMobile ? 7 : 8, 
                            height: isMobile ? 7 : 8, 
                            borderRadius: '50%', 
                            background: GRADIENT, 
                            flexShrink: 0, 
                            display: 'inline-block' 
                          }} />
                        )}
                        {col.color ? (
                          <span style={{ 
                            fontFamily: F.mono, 
                            fontSize: isMobile ? 8 : 9, 
                            fontWeight: 700, 
                            letterSpacing: '0.12em', 
                            textTransform: 'uppercase', 
                            color: col.color 
                          }}>
                            {col.label}
                          </span>
                        ) : (
                          <span style={{ 
                            fontFamily: F.mono, 
                            fontSize: isMobile ? 8 : 9, 
                            fontWeight: 700, 
                            letterSpacing: '0.12em', 
                            textTransform: 'uppercase', 
                            ...GRADIENT_TEXT 
                          }}>
                            {col.label}
                          </span>
                        )}
                      </div>
                      <p style={{
                        fontFamily: F.body,
                        fontSize: isMobile ? 12 : 13, 
                        fontWeight: 400,
                        color: '#374151', 
                        lineHeight: 1.65, 
                        margin: 0,
                        wordBreak: 'break-word',
                      }}>
                        {card[col.key]}
                      </p>
                    </div>
                  ))}
                </div>

                {/* ── Win Strategy ── */}
                {card.winStrategy && (
                  <div style={{
                    margin: isMobile ? '0 16px 16px' : (isTablet ? '0 20px 20px' : '0 24px 24px'),
                    padding: isMobile ? '14px 16px' : '16px 20px', 
                    borderRadius: isMobile ? 10 : 12,
                    background: '#f0fdf4', 
                    border: '1px solid #bbf7d0',
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: isMobile ? 12 : 14,
                  }}>
                    <div style={{
                      width: isMobile ? 28 : 32, 
                      height: isMobile ? 28 : 32, 
                      borderRadius: isMobile ? 7 : 8, 
                      flexShrink: 0,
                      background: '#16a34a',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                    }}>
                      <svg 
                        width={isMobile ? 12 : 14} 
                        height={isMobile ? 12 : 14} 
                        viewBox="0 0 14 14" 
                        fill="none"
                      >
                        <path 
                          d="M2.5 7L5.5 10L11.5 4" 
                          stroke="#ffffff" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                        />
                      </svg>
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{
                        fontFamily: F.mono,
                        fontSize: isMobile ? 8 : 9, 
                        fontWeight: 700,
                        letterSpacing: '0.12em', 
                        textTransform: 'uppercase',
                        color: '#16a34a', 
                        margin: isMobile ? '0 0 5px' : '0 0 6px',
                      }}>
                        Win Strategy
                      </p>
                      <p style={{
                        fontFamily: F.body,
                        fontSize: isMobile ? 12.5 : 13.5, 
                        fontWeight: 400,
                        color: '#14532d', 
                        lineHeight: 1.65, 
                        margin: 0,
                        wordBreak: 'break-word',
                      }}>
                        {(card.winStrategy || '').replace(/^[✓✔☑\s]+/, '')}
                      </p>
                    </div>
                  </div>
                )}

              </div>
            );
          })}
        </div>
      ) : (
        <div style={{
          background: '#ffffff', 
          borderRadius: isMobile ? 14 : 20,
          border: '0.5px solid rgba(0,0,0,0.06)', 
          padding: isMobile ? 20 : 28,
        }}>
          <EmptyState 
            icon="Shield" 
            title="No battle cards" 
            desc="Competitive battle cards will populate from your HTML report." 
          />
        </div>
      )}
    </div>
  );
};

export default BattleCards;